

import { getSelectedAccountId } from './accountScope';
import type {
  AffiliateSummary,
  DatabaseRecordsPage,
  DeploymentMetrics,
  EntityDescription,
  MetricSeries,
  Purchase,
  ReferralData,
  SecretsOverview,
  ServiceStatus,
  ShippingRate,
  ShippingStatus,
  SonarQubeHotspot,
  SonarQubeIssue,
  SonarQubeMetrics,
  SonarQubeProject,
  StripeProduct,
  TableOverview,
} from '../types/api';

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
}

interface AffiliateData {
  name: string;
  email: string;
  phone?: string;
  commissionRate?: number;
}

// `channel` is required by the endpoint alongside name and url; the rest are
// optional attribution and cost fields it stores when present.
interface QRCodeData {
  name: string;
  channel: string;
  url: string;
  description?: string;
  bottleType?: string;
  batchNumber?: string;
  campaignId?: string;
  campaignName?: string;
  costPerClick?: number;
  totalSpent?: number;
}

// Mirrors what POST /api/qr-tracking/create-bottle-batch actually requires.
// The previous shape named a `productType` the endpoint has never read, and
// omitted baseUrl, which it rejects the request without.
interface BottleBatchData {
  bottleType: string;
  batchNumber: string;
  quantity: number;
  baseUrl: string;
  campaignId?: string;
  campaignName?: string;
  productionDate?: string;
  expiryDate?: string;
}

interface RoleData {
  name: string;
  permissions: string[];
  description?: string;
}

interface PermissionData {
  name: string;
  description?: string;
  enabled: boolean;
}

class ApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || 'http://localhost:3001';
  }

  private async request<T = unknown>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;
    const token = (await import('./auth.js')).default.getToken();
    const accountId = getSelectedAccountId();

    const isFormData = options.body instanceof FormData;

    const config: RequestInit = {
      headers: {
        // Omitted for FormData: the browser has to set the multipart
        // boundary itself, and naming the type here strips it.
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...(token && { Authorization: `Bearer ${token}` }),
        // Which account this request acts for. Sent on everything rather
        // than only the scoped routes: the unscoped ones ignore it, and a
        // header that appears and disappears depending on the endpoint is
        // harder to reason about than one that is always present.
        ...(accountId && { 'X-Account-Id': accountId }),
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      // A dead session is not a failure of the thing you asked for.
      //
      // Without this, an expired or invalidated token turns every panel on the
      // page into its own error — "Failed to fetch dashboard data", "Failed to
      // fetch transactions" — and the real cause, a 401 saying "Access token
      // required", appears nowhere. Rotating JWT_SECRET does exactly that to
      // every session issued before the rotation.
      //
      // The login form is excluded: a wrong password is a 401 that the form
      // itself should report, not a reason to reload the page.
      // Only when a token was actually sent. A 401 with no token is the
      // expected answer for a signed-out caller, and logging out again in
      // response means clearing nothing and redirecting — which, if anything
      // on the page then makes another request, goes round for ever. The loop
      // does not need two paths to exist; one is enough.
      if (response.status === 401 && token && !endpoint.includes('/admin/login')) {
        const auth = (await import('./auth.js')).default;
        auth.logout();
        throw new Error('Your session has ended. Please sign in again.');
      }

      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Authentication
  async login(username: string, password: string): Promise<ApiResponse> {
    return this.request('/api/admin/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  }

  async verifyToken(): Promise<ApiResponse> {
    return this.request('/api/admin/verify-token');
  }

  // Admin operations
  async getAdminProfile(): Promise<ApiResponse> {
    return this.request('/api/admin/profile');
  }

  async getReferrals(filters?: { status?: string; affiliateId?: string; page?: number; limit?: number }): Promise<ApiResponse<{ referrals: ReferralData[] }>> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.affiliateId) params.append('affiliateId', filters.affiliateId);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    
    const queryString = params.toString();
    const url = queryString ? `/api/admin/referrals?${queryString}` : '/api/admin/referrals';
    return this.request(url);
  }

  async updateReferralStatus(referralId: string, status: string, commission?: number): Promise<ApiResponse> {
    return this.request(`/api/admin/referral/${referralId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, commission }),
    });
  }

  async bulkUpdateReferralStatus(referralIds: string[], status: string, commission?: number): Promise<ApiResponse> {
    return this.request('/api/admin/referrals/bulk-update', {
      method: 'PATCH',
      body: JSON.stringify({ referralIds, status, commission }),
    });
  }

  async getAffiliates(): Promise<ApiResponse<{ affiliates: AffiliateSummary[] }>> {
    return this.request('/api/admin/affiliates');
  }

  // Production runs
  async getBatches(): Promise<ApiResponse> {
    return this.request('/api/batches');
  }

  async createBatch(data: {
    batchNumber: string; productId: string; productSize: string; plannedUnits: number; shipsIn?: string;
  }): Promise<ApiResponse> {
    return this.request('/api/batches', { method: 'POST', body: JSON.stringify(data) });
  }

  async recordBatchBottling(batchId: string, bottledUnits: number): Promise<ApiResponse> {
    return this.request(`/api/batches/${batchId}/bottled`, {
      method: 'POST',
      body: JSON.stringify({ bottledUnits }),
    });
  }

  async updateBatch(batchId: string, changes: Record<string, unknown>): Promise<ApiResponse> {
    return this.request(`/api/batches/${batchId}`, { method: 'PATCH', body: JSON.stringify(changes) });
  }

  async getSubscribers(params?: { limit?: number; cursor?: string }): Promise<ApiResponse> {
    const query = new URLSearchParams();
    if (params?.limit) query.append('limit', String(params.limit));
    if (params?.cursor) query.append('cursor', params.cursor);

    const queryString = query.toString();
    return this.request(queryString ? `/api/subscribe?${queryString}` : '/api/subscribe');
  }

  // The email-and-text offer: everyone who claimed the $5 code, newest first,
  // with whether each message actually went out. Paged by an opaque cursor.
  async getLeads(params?: { limit?: number; cursor?: string }): Promise<ApiResponse> {
    const query = new URLSearchParams();
    if (params?.limit) query.append('limit', String(params.limit));
    if (params?.cursor) query.append('cursor', params.cursor);

    const queryString = query.toString();
    return this.request(queryString ? `/api/leads?${queryString}` : '/api/leads');
  }

  // POSTs to /affiliate/create, not /affiliates. The latter exists only as a
  // GET, so this method used to 404 on every call — nothing in the console
  // reached it, and the one page that created affiliates called fetch directly.
  //
  // `walletInput` rather than a wallet address: the endpoint resolves an ENS
  // name as well as a hex address. Omit discountCode to have it derived from
  // the affiliate's name.
  async createAffiliate(data: {
    name: string;
    email: string;
    walletInput: string;
    createDiscount: boolean;
    discountCode?: string;
  }): Promise<ApiResponse & { warning?: string }> {
    return this.request('/api/admin/affiliate/create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAffiliate(id: string, data: Partial<AffiliateData>): Promise<ApiResponse> {
    return this.request(`/api/admin/affiliates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteAffiliate(id: string): Promise<ApiResponse> {
    return this.request(`/api/admin/affiliates/${id}`, {
      method: 'DELETE',
    });
  }

  // QR Code operations
  //
  // These endpoints answer with a named key at the top level — `qrCodes`,
  // `analytics` — rather than nesting under `data`. Typing them says so, so a
  // caller reaching for response.data gets a compile error instead of
  // undefined at runtime. That exact mismatch silently emptied every Sales
  // tab once already.
  async getQRCodes(): Promise<ApiResponse & { qrCodes?: unknown[] }> {
    return this.request('/api/qr-tracking/qr-codes') as Promise<ApiResponse & { qrCodes?: unknown[] }>;
  }

  async getQRAnalytics(): Promise<ApiResponse & { analytics?: unknown }> {
    return this.request('/api/qr-tracking/qr-analytics') as Promise<ApiResponse & { analytics?: unknown }>;
  }

  async createQRCode(data: QRCodeData): Promise<ApiResponse> {
    return this.request('/api/qr-tracking/create-qr-code', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createBottleBatch(data: BottleBatchData): Promise<ApiResponse> {
    return this.request('/api/qr-tracking/create-bottle-batch', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Stripe operations
  async createCheckoutSession(data: {
    productId: string;
    priceId: string;
    successUrl: string;
    cancelUrl: string;
  }): Promise<ApiResponse> {
    return this.request('/api/stripe/create-checkout-session', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getPaymentIntent(id: string): Promise<ApiResponse> {
    return this.request(`/api/stripe/payment-intent/${id}`);
  }

  async getStripeProducts(): Promise<ApiResponse<{ products: StripeProduct[] }>> {
    return this.request('/api/stripe/admin/products');
  }

  // Product Management API endpoints
  async createProduct(productData: {
    name: string;
    description: string;
    images: string[];
    metadata: Record<string, string>;
    prices: {
      currency: string;
      unitAmount: number;
      recurring?: {
        interval: string;
        intervalCount: number;
      };
    }[];
  }): Promise<ApiResponse> {
    return this.request('/api/stripe/products', {
      method: 'POST',
      body: JSON.stringify(productData),
    });
  }

  async updateProduct(productId: string, productData: {
    name?: string;
    description?: string;
    images?: string[];
    metadata?: Record<string, string>;
    active?: boolean;
  }): Promise<ApiResponse> {
    return this.request(`/api/stripe/products/${productId}`, {
      method: 'PUT',
      body: JSON.stringify(productData),
    });
  }

  async deleteProduct(productId: string): Promise<ApiResponse> {
    return this.request(`/api/stripe/products/${productId}`, {
      method: 'DELETE',
    });
  }

  async updateProductVisibility(productId: string, visible: boolean): Promise<ApiResponse> {
    return this.request(`/api/stripe/products/${productId}/visibility`, {
      method: 'PATCH',
      body: JSON.stringify({ visible }),
    });
  }

  // ===== SALES & TRANSACTIONS API ENDPOINTS =====

  // Sales Analytics
  async getSalesAnalytics(startDate?: string, endDate?: string, groupBy?: string): Promise<ApiResponse> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (groupBy) params.append('groupBy', groupBy);
    
    return this.request(`/api/stripe/sales-analytics?${params}`);
  }

  // Transactions
  async getTransactions(params: {
    limit?: number;
    startingAfter?: string;
    endingBefore?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
  } = {}): Promise<ApiResponse> {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) queryParams.append(key, value.toString());
    });
    
    return this.request(`/api/stripe/transactions?${queryParams}`);
  }

  // Customer Analytics
  async getCustomerAnalytics(startDate?: string, endDate?: string): Promise<ApiResponse> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    return this.request(`/api/stripe/customer-analytics?${params}`);
  }

  // Refund Analytics
  async getRefundAnalytics(startDate?: string, endDate?: string): Promise<ApiResponse> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    return this.request(`/api/stripe/refund-analytics?${params}`);
  }

  // Real-time Dashboard Data
  async getDashboardData(): Promise<ApiResponse> {
    return this.request('/api/stripe/dashboard-data');
  }

  // Batch Analytics
  async getBatchAnalytics(batchNumber: string): Promise<ApiResponse> {
    return this.request(`/api/qr-tracking/batch-analytics/${batchNumber}`);
  }

  // ROI Analysis
  async getROIAnalysis(): Promise<ApiResponse> {
    return this.request('/api/qr-tracking/qr-roi');
  }

  /**
   * Where paid orders came from, over the last `days`.
   *
   * Separate from the QR endpoints on purpose: QR counts scans, which only
   * exist once codes are printed and used, while this reads orders that have
   * already happened.
   */
  async getAttributionReport(days = 30): Promise<ApiResponse> {
    return this.request(`/api/attribution/report?days=${days}`);
  }

  /**
   * How each affiliate is performing over the last `days`.
   *
   * The whole funnel per affiliate — link clicks, distinct people, list
   * signups, paid orders, and what is owed on them. This is what the Traffic
   * Drivers affiliate tab reads.
   */
  async getAffiliatePerformance(days = 30): Promise<ApiResponse & { report?: unknown }> {
    return this.request(`/api/affiliate/performance?days=${days}`);
  }

  /**
   * Attach a $5-off discount code to an affiliate that has none.
   *
   * Leave `discountCode` blank to let the server derive one from their name
   * ("Ada Lovelace" becomes ADA5). Refused with 409 if they already have a
   * code — reissuing would leave the old one live in Stripe and in whatever
   * the creator has already published.
   */
  async createAffiliateDiscount(
    affiliateId: string,
    discountCode?: string,
  ): Promise<ApiResponse & { data?: { discountCode: string; discountAmount: number } }> {
    return this.request(`/api/admin/affiliate/${affiliateId}/discount`, {
      method: 'POST',
      body: JSON.stringify(discountCode ? { discountCode } : {}),
    });
  }

  /**
   * The commission ledger — one row per order an affiliate is owed on.
   */
  async getAffiliateCommissions(filters: {
    status?: string;
    affiliateId?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<ApiResponse> {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.affiliateId) params.append('affiliateId', filters.affiliateId);
    if (filters.page) params.append('page', String(filters.page));
    if (filters.limit) params.append('limit', String(filters.limit));

    const query = params.toString();
    return this.request(`/api/affiliate/commissions${query ? `?${query}` : ''}`);
  }

  /**
   * Settle a commission, or reverse one.
   *
   * Only 'paid' and 'void' are accepted; the server refuses anything that is
   * not currently pending, so a double-click cannot pay twice.
   */
  async updateAffiliateCommission(
    commissionId: string,
    status: 'paid' | 'void',
    note?: string,
  ): Promise<ApiResponse> {
    return this.request(`/api/affiliate/commissions/${commissionId}`, {
      method: 'PATCH',
      body: JSON.stringify({ status, ...(note ? { note } : {}) }),
    });
  }

  /**
   * The URL a QR code must encode for a scan to be recorded.
   *
   * Built from the API base rather than the dashboard's own origin. Pointing
   * at the dashboard returns its index.html — the SPA rewrite answers every
   * path — so the scan is never counted and the person scanning gets an admin
   * login screen.
   */
  qrTrackingUrl(code: string): string {
    return `${this.baseUrl}/api/qr-tracking/track/${encodeURIComponent(code)}`;
  }

  // ===== SYS & DEV OPS API ENDPOINTS =====

  // Build Management
  async getBuilds(): Promise<ApiResponse> {
    return this.request('/api/sysdevops/builds');
  }

  // Whether the backend can reach ECS and GitHub Actions. The UI uses this to
  // explain an empty panel rather than showing one with no reason.
  async getDevOpsConfig(): Promise<ApiResponse> {
    return this.request('/api/sysdevops/builds/config');
  }

  // Coverage Analysis
  async getCoverageData(): Promise<ApiResponse> {
    return this.request('/api/sysdevops/coverage');
  }

  async getCoverageReport(project: string): Promise<ApiResponse> {
    return this.request(`/api/sysdevops/coverage/${encodeURIComponent(project)}`);
  }

  // Quality Metrics
  async getQualityMetrics(): Promise<ApiResponse> {
    return this.request('/api/sysdevops/quality');
  }

  async runQualityScan(project: string): Promise<ApiResponse> {
    return this.request('/api/sysdevops/quality/scan', {
      method: 'POST',
      body: JSON.stringify({ project }),
    });
  }

  // Security Analysis
  async getSecurityVulnerabilities(): Promise<ApiResponse> {
    return this.request('/api/sysdevops/security/vulnerabilities');
  }

  async getSecurityMetrics(): Promise<ApiResponse> {
    return this.request('/api/sysdevops/security/metrics');
  }

  // System Monitoring
  async getSystemMetrics(): Promise<ApiResponse> {
    return this.request('/api/sysdevops/system/metrics');
  }

  async getSystemLogs(limit?: number): Promise<ApiResponse> {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    return this.request(`/api/sysdevops/system/logs?${params}`);
  }

  // Deployment Management
  //
  // Backed by ECS and GitHub Actions. A deploy is REQUESTED here, not
  // performed: the backend dispatches the environment's workflow, and
  // production still refuses a commit staging has not deployed.
  async getDeployments(environment?: string): Promise<ApiResponse> {
    const query = environment ? `?environment=${encodeURIComponent(environment)}` : '';
    return this.request(`/api/sysdevops/deployments${query}`);
  }

  // One row per commit — recent main, open pull request heads, anything
  // deployed — with its state in each environment and the action each
  // environment's button would take. Every action goes through
  // triggerDeployment with that commit.
  async getVersions(): Promise<ApiResponse> {
    return this.request('/api/sysdevops/deployments/versions');
  }

  // What this environment is running right now, straight from its ECS service.
  async getDeploymentStatus(): Promise<ApiResponse<ServiceStatus>> {
    return this.request('/api/sysdevops/deployments/status');
  }

  async triggerDeployment(environment: string, sha?: string): Promise<ApiResponse> {
    return this.request('/api/sysdevops/deployments/trigger', {
      method: 'POST',
      body: JSON.stringify({ environment, sha }),
    });
  }

  // Moves the service back one task definition revision. Takes no id: there is
  // exactly one thing to roll back — whatever is deployed now.
  async rollbackDeployment(): Promise<ApiResponse> {
    return this.request('/api/sysdevops/deployments/rollback', {
      method: 'POST',
    });
  }

  async getDeploymentMetrics(): Promise<ApiResponse<DeploymentMetrics>> {
    return this.request('/api/sysdevops/deployments/metrics');
  }

  // ===== USER MANAGEMENT API ENDPOINTS =====

  // Chill Setter Management
  async getChillSetters(): Promise<ApiResponse> {
    return this.request('/api/admin/chill-setters');
  }

  async getChillSetterApplications(): Promise<ApiResponse> {
    return this.request('/api/admin/chill-setters/applications');
  }

  async approveChillSetter(applicationId: string): Promise<ApiResponse> {
    return this.request(`/api/admin/chill-setters/applications/${applicationId}/approve`, {
      method: 'POST',
    });
  }

  async rejectChillSetter(applicationId: string, reason?: string): Promise<ApiResponse> {
    return this.request(`/api/admin/chill-setters/applications/${applicationId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  // Roles & Permissions
  async getRoles(): Promise<ApiResponse> {
    return this.request('/api/admin/roles');
  }

  async updateRole(roleId: string, data: Partial<RoleData>): Promise<ApiResponse> {
    return this.request(`/api/admin/roles/${roleId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getPermissions(): Promise<ApiResponse> {
    return this.request('/api/admin/permissions');
  }

  async updatePermission(permissionId: string, data: Partial<PermissionData>): Promise<ApiResponse> {
    return this.request(`/api/admin/permissions/${permissionId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // ===== SONARQUBE API ENDPOINTS =====

  // SonarQube System Status
  async getSonarQubeStatus(): Promise<ApiResponse> {
    return this.request('/api/sysdevops/sonarqube/status');
  }

  // SonarQube Projects
  async getSonarQubeProjects(): Promise<ApiResponse<{ components: SonarQubeProject[] }>> {
    return this.request('/api/sysdevops/sonarqube/projects');
  }

  // SonarQube Project Report
  async getSonarQubeProjectReport(projectKey: string): Promise<ApiResponse> {
    return this.request(`/api/sysdevops/sonarqube/project/${projectKey}`);
  }

  // SonarQube Metrics
  async getSonarQubeMetrics(projectKey: string): Promise<ApiResponse<SonarQubeMetrics>> {
    return this.request(`/api/sysdevops/sonarqube/metrics/${projectKey}`);
  }

  // SonarQube Issues
  async getSonarQubeIssues(projectKey: string, severity?: string, status?: string): Promise<ApiResponse<{ issues: SonarQubeIssue[] }>> {
    const params = new URLSearchParams();
    if (severity) params.append('severity', severity);
    if (status) params.append('status', status);
    return this.request(`/api/sysdevops/sonarqube/issues/${projectKey}?${params}`);
  }

  // SonarQube Security Hotspots
  async getSonarQubeHotspots(projectKey: string, status?: string): Promise<ApiResponse<{ hotspots: SonarQubeHotspot[] }>> {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    return this.request(`/api/sysdevops/sonarqube/hotspots/${projectKey}?${params}`);
  }

  // Trigger SonarQube Analysis
  async triggerSonarQubeAnalysis(projectKey: string, branch?: string): Promise<ApiResponse> {
    return this.request(`/api/sysdevops/sonarqube/analyze/${projectKey}`, {
      method: 'POST',
      body: JSON.stringify({ branch }),
    });
  }

  // ===== SHIPPING API ENDPOINTS =====

  // Purchase Management
  async getPurchases(params: {
    page?: number;
    limit?: number;
    status?: string;
    productId?: string;
    // Fulfilment state, as opposed to `status` which is the Stripe payment state.
    orderStatus?: string;
    search?: string;
  } = {}): Promise<ApiResponse<{ purchases: Purchase[] }>> {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) queryParams.append(key, value.toString());
    });
    
    return this.request(`/api/purchases?${queryParams}`);
  }

  // Live Stripe detail for one order: receipt, card, refunds, and the fee
  // breakdown. The stored order is a checkout-time snapshot and knows none of it.
  async getOrderStripeDetail(purchaseId: string): Promise<ApiResponse> {
    return this.request(`/api/purchases/${purchaseId}/stripe`);
  }

  // Reconcile a stored order against Stripe; reports what disagreed.
  async syncOrderWithStripe(purchaseId: string): Promise<ApiResponse> {
    return this.request(`/api/purchases/${purchaseId}/sync`, { method: 'POST' });
  }

  // Resends the customer's confirmation. Answers 200 with sent:false when the
  // provider refuses, so the caller can show the reason rather than a generic
  // failure.
  async resendOrderConfirmation(purchaseId: string): Promise<ApiResponse> {
    return this.request(`/api/purchases/${purchaseId}/resend-confirmation`, { method: 'POST' });
  }

  async updateOrderStatus(purchaseId: string, body: {
    orderStatus: string;
    shippingDate?: string;
    trackingNumber?: string;
  }): Promise<ApiResponse> {
    return this.request(`/api/purchases/${purchaseId}/status`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  async getPurchaseStats(startDate?: string, endDate?: string): Promise<ApiResponse> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    return this.request(`/api/purchases/stats?${params}`);
  }

  // Shipping Management
  async getShippingRates(purchaseId: string): Promise<ApiResponse<Purchase & { rates: ShippingRate[] }>> {
    return this.request(`/api/shipping/rates/${purchaseId}`, {
      method: 'POST',
    });
  }

  async createShippingLabel(purchaseId: string, rateId: string, carrier?: string): Promise<ApiResponse<{ trackingNumber: string }>> {
    return this.request(`/api/shipping/label/${purchaseId}`, {
      method: 'POST',
      body: JSON.stringify({ rateId, carrier }),
    });
  }

  async trackShipment(trackingNumber: string, carrier: string): Promise<ApiResponse> {
    return this.request(`/api/shipping/track/${trackingNumber}?carrier=${carrier}`);
  }

  async getCarriers(): Promise<ApiResponse> {
    return this.request('/api/shipping/carriers');
  }

  async validateAddress(addressData: {
    name: string;
    street1: string;
    street2?: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    phone?: string;
    email?: string;
  }): Promise<ApiResponse> {
    return this.request('/api/shipping/validate-address', {
      method: 'POST',
      body: JSON.stringify(addressData),
    });
  }

  async getShippingStatus(): Promise<ApiResponse<ShippingStatus>> {
    return this.request('/api/shipping/status');
  }

  async getShippingHistory(purchaseId: string): Promise<ApiResponse> {
    return this.request(`/api/shipping/history/${purchaseId}`);
  }

  async bulkCreateLabels(purchaseIds: string[], rateId: string): Promise<ApiResponse> {
    return this.request('/api/shipping/bulk/labels', {
      method: 'POST',
      body: JSON.stringify({ purchaseIds, rateId }),
    });
  }

  // ===== INSTAGRAM AGENT =====

  // Connection
  async getInstagramConnectionStatus(): Promise<ApiResponse> {
    return this.request('/api/instagram/connection/status');
  }

  async getInstagramOAuthUrl(): Promise<ApiResponse> {
    return this.request('/api/instagram/connection/oauth-url');
  }

  async refreshInstagramToken(): Promise<ApiResponse> {
    return this.request('/api/instagram/connection/refresh-token', { method: 'POST' });
  }

  async disconnectInstagram(): Promise<ApiResponse> {
    return this.request('/api/instagram/connection/disconnect', { method: 'DELETE' });
  }

  // Content generation
  async getBrandAssets(): Promise<ApiResponse> {
    return this.request('/api/instagram/content/brand-assets');
  }

  async generateCaption(theme: string, context?: string): Promise<ApiResponse> {
    return this.request('/api/instagram/content/generate-caption', {
      method: 'POST',
      body: JSON.stringify({ theme, context }),
    });
  }

  async suggestHashtags(caption: string, theme?: string): Promise<ApiResponse> {
    return this.request('/api/instagram/content/suggest-hashtags', {
      method: 'POST',
      body: JSON.stringify({ caption, theme }),
    });
  }

  async optimizeCaption(caption: string, performanceData?: Record<string, unknown>): Promise<ApiResponse> {
    return this.request('/api/instagram/content/optimize-caption', {
      method: 'POST',
      body: JSON.stringify({ caption, performanceData }),
    });
  }

  async generateCampaignCopy(objective: string, targetAudience?: string, product?: string): Promise<ApiResponse> {
    return this.request('/api/instagram/content/generate-campaign-copy', {
      method: 'POST',
      body: JSON.stringify({ objective, targetAudience, product }),
    });
  }

  // Trend mining (Glystn) + strategy (Gemini)
  async getTrendStatus(): Promise<ApiResponse> {
    return this.request('/api/instagram/trends/status');
  }

  async getGlystnTools(): Promise<ApiResponse> {
    return this.request('/api/instagram/trends/tools');
  }

  async searchTrends(topic: string, mode: 'semantic' | 'keyword' = 'semantic', limit?: number): Promise<ApiResponse> {
    return this.request('/api/instagram/trends/search', {
      method: 'POST',
      body: JSON.stringify({ topic, mode, limit }),
    });
  }

  async discoverCreators(topic: string, limit?: number): Promise<ApiResponse> {
    return this.request('/api/instagram/trends/creators', {
      method: 'POST',
      body: JSON.stringify({ topic, limit }),
    });
  }

  // Mines and reasons in one call.
  async generateTrendStrategy(topic: string, mode: 'semantic' | 'keyword' = 'semantic', limit?: number): Promise<ApiResponse> {
    return this.request('/api/instagram/trends/strategy', {
      method: 'POST',
      body: JSON.stringify({ topic, mode, limit }),
    });
  }

  async generateMediaPrompts(topic: string, context?: string, count?: number): Promise<ApiResponse> {
    return this.request('/api/instagram/trends/media-prompts', {
      method: 'POST',
      body: JSON.stringify({ topic, context, count }),
    });
  }

  // Generated media
  // referenceUrl seeds generation from a real brand asset, so the product in
  // the result is the actual one rather than a generated likeness.
  // Accounts — the brands this console operates on behalf of. Naming only;
  // see AccountsSection for what that does and does not mean.
  async getAccounts(): Promise<ApiResponse> {
    return this.request('/api/accounts');
  }

  async createAccount(name: string, slug?: string): Promise<ApiResponse> {
    return this.request('/api/accounts', {
      method: 'POST',
      body: JSON.stringify({ name, ...(slug ? { slug } : {}) }),
    });
  }

  async generateImage(
    prompt: string, aspectRatio?: string, referenceUrl?: string, provider?: string,
  ): Promise<ApiResponse> {
    return this.request('/api/instagram/media/generate-image', {
      method: 'POST',
      body: JSON.stringify({ prompt, aspectRatio, referenceUrl, provider }),
    });
  }

  async generateVideo(
    prompt: string, aspectRatio?: string, durationSeconds?: number,
    referenceUrl?: string, provider?: string,
  ): Promise<ApiResponse> {
    return this.request('/api/instagram/media/generate-video', {
      method: 'POST',
      body: JSON.stringify({ prompt, aspectRatio, durationSeconds, referenceUrl, provider }),
    });
  }

  // Which generation backends are usable, and why one is not. Drives the
  // picker, so it is read before an admin writes a brief rather than after.
  async getMediaProviders(): Promise<ApiResponse> {
    return this.request('/api/instagram/media/providers');
  }

  // Stores the reel a recreate will be modelled on and returns its URL.
  async uploadInspiration(file: File): Promise<ApiResponse> {
    const form = new FormData();
    form.append('video', file);

    return this.request('/api/instagram/media/inspiration', {
      method: 'POST',
      body: form,
    });
  }

  /**
   * Put an asset the studio did not generate into the media library.
   *
   * Comes back as a completed media job, the same row shape generation
   * produces, so the caller does not have to poll it or treat it specially.
   */
  async uploadMedia(file: File, description?: string): Promise<ApiResponse> {
    const form = new FormData();
    form.append('file', file);
    if (description?.trim()) form.append('description', description.trim());

    return this.request('/api/instagram/media/upload', {
      method: 'POST',
      body: form,
    });
  }

  // Starts a reel modelled on an inspiration video with the product attached.
  // Returns a job row polled through getMediaJob, same as any video.
  async recreateReel(
    sourceVideoUrl: string, prompt?: string, aspectRatio?: string,
  ): Promise<ApiResponse> {
    return this.request('/api/instagram/media/recreate', {
      method: 'POST',
      body: JSON.stringify({ sourceVideoUrl, prompt, aspectRatio }),
    });
  }

  // Poll this while a video job reports "processing".
  async getMediaJob(jobId: string): Promise<ApiResponse> {
    return this.request(`/api/instagram/media/jobs/${jobId}`);
  }

  async getMediaJobs(filters?: { kind?: string; status?: string; limit?: number }): Promise<ApiResponse> {
    const params = new URLSearchParams();
    if (filters?.kind) params.append('kind', filters.kind);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.limit) params.append('limit', String(filters.limit));
    const qs = params.toString();
    return this.request(`/api/instagram/media/jobs${qs ? `?${qs}` : ''}`);
  }

  async deleteMediaJob(jobId: string): Promise<ApiResponse> {
    return this.request(`/api/instagram/media/jobs/${jobId}`, { method: 'DELETE' });
  }

  // Posts
  async getInstagramPosts(filters?: { status?: string; page?: number; limit?: number }): Promise<ApiResponse> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.page) params.append('page', String(filters.page));
    if (filters?.limit) params.append('limit', String(filters.limit));
    const qs = params.toString();
    return this.request(`/api/instagram/posts${qs ? `?${qs}` : ''}`);
  }

  async getInstagramPost(postId: string): Promise<ApiResponse> {
    return this.request(`/api/instagram/posts/${postId}`);
  }

  async createInstagramPost(data: Record<string, unknown>): Promise<ApiResponse> {
    return this.request('/api/instagram/posts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateInstagramPost(postId: string, data: Record<string, unknown>): Promise<ApiResponse> {
    return this.request(`/api/instagram/posts/${postId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteInstagramPost(postId: string): Promise<ApiResponse> {
    return this.request(`/api/instagram/posts/${postId}`, { method: 'DELETE' });
  }

  async submitPostForApproval(postId: string): Promise<ApiResponse> {
    return this.request(`/api/instagram/posts/${postId}/submit-for-approval`, { method: 'POST' });
  }

  async approveInstagramPost(postId: string, note?: string): Promise<ApiResponse> {
    return this.request(`/api/instagram/posts/${postId}/approve`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    });
  }

  async rejectInstagramPost(postId: string, note?: string): Promise<ApiResponse> {
    return this.request(`/api/instagram/posts/${postId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    });
  }

  async scheduleInstagramPost(postId: string, scheduledFor: string): Promise<ApiResponse> {
    return this.request(`/api/instagram/posts/${postId}/schedule`, {
      method: 'POST',
      body: JSON.stringify({ scheduledFor }),
    });
  }

  async publishInstagramPost(postId: string): Promise<ApiResponse> {
    return this.request(`/api/instagram/posts/${postId}/publish`, { method: 'POST' });
  }

  /**
   * Take a scheduled post back out of the queue. It returns to `approved`,
   * not to draft — it was already approved before it was scheduled.
   */
  async unscheduleInstagramPost(postId: string): Promise<ApiResponse> {
    return this.request(`/api/instagram/posts/${postId}/unschedule`, { method: 'POST' });
  }

  /**
   * What is queued to go out, soonest first, with each time rendered in the
   * brand timezone by the server so the two never disagree.
   */
  async getInstagramSchedulerQueue(): Promise<ApiResponse & { posts?: unknown[]; timezone?: string }> {
    return this.request('/api/instagram/scheduler/queue');
  }

  // Content calendar
  async getContentCalendar(month?: number, year?: number): Promise<ApiResponse> {
    const params = new URLSearchParams();
    if (month) params.append('month', String(month));
    if (year) params.append('year', String(year));
    const qs = params.toString();
    return this.request(`/api/instagram/calendar${qs ? `?${qs}` : ''}`);
  }

  async generateContentCalendar(weekStartDate: string, days?: number): Promise<ApiResponse> {
    return this.request('/api/instagram/calendar/generate', {
      method: 'POST',
      body: JSON.stringify({ weekStartDate, days }),
    });
  }

  async acceptCalendarEntry(entryId: string): Promise<ApiResponse> {
    return this.request(`/api/instagram/calendar/${entryId}/accept`, { method: 'POST' });
  }

  async skipCalendarEntry(entryId: string): Promise<ApiResponse> {
    return this.request(`/api/instagram/calendar/${entryId}/skip`, { method: 'POST' });
  }

  // Campaigns
  async getInstagramCampaigns(filters?: { status?: string; page?: number; limit?: number }): Promise<ApiResponse> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.page) params.append('page', String(filters.page));
    if (filters?.limit) params.append('limit', String(filters.limit));
    const qs = params.toString();
    return this.request(`/api/instagram/campaigns${qs ? `?${qs}` : ''}`);
  }

  async getInstagramCampaign(campaignId: string): Promise<ApiResponse> {
    return this.request(`/api/instagram/campaigns/${campaignId}`);
  }

  async createInstagramCampaign(data: Record<string, unknown>): Promise<ApiResponse> {
    return this.request('/api/instagram/campaigns', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateInstagramCampaign(campaignId: string, data: Record<string, unknown>): Promise<ApiResponse> {
    return this.request(`/api/instagram/campaigns/${campaignId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteInstagramCampaign(campaignId: string): Promise<ApiResponse> {
    return this.request(`/api/instagram/campaigns/${campaignId}`, { method: 'DELETE' });
  }

  async launchInstagramCampaign(campaignId: string): Promise<ApiResponse> {
    return this.request(`/api/instagram/campaigns/${campaignId}/launch`, { method: 'POST' });
  }

  async pauseInstagramCampaign(campaignId: string): Promise<ApiResponse> {
    return this.request(`/api/instagram/campaigns/${campaignId}/pause`, { method: 'POST' });
  }

  async resumeInstagramCampaign(campaignId: string): Promise<ApiResponse> {
    return this.request(`/api/instagram/campaigns/${campaignId}/resume`, { method: 'POST' });
  }

  async syncCampaignPerformance(campaignId: string): Promise<ApiResponse> {
    return this.request(`/api/instagram/campaigns/${campaignId}/sync-performance`, { method: 'POST' });
  }

  async getCampaignBudgetSummary(): Promise<ApiResponse> {
    return this.request('/api/instagram/campaigns/budget-summary');
  }

  // Analytics
  async getInstagramAnalyticsOverview(): Promise<ApiResponse> {
    return this.request('/api/instagram/analytics/overview');
  }

  async getInstagramPostAnalytics(): Promise<ApiResponse> {
    return this.request('/api/instagram/analytics/posts');
  }

  async getInstagramCampaignAnalytics(): Promise<ApiResponse> {
    return this.request('/api/instagram/analytics/campaigns');
  }

  // Runtime secrets.
  //
  // Listing never returns a value — only metadata. revealSecret is the one
  // call that does, which is why it is a separate request per key rather than
  // a flag on the list: a value crosses the wire only when someone asks for
  // that key by name.
  async getSecrets(): Promise<ApiResponse<SecretsOverview>> {
    return this.request('/api/sysdevops/secrets');
  }

  async revealSecret(environment: string, key: string): Promise<ApiResponse<{ value: string }>> {
    return this.request(`/api/sysdevops/secrets/${environment}/${encodeURIComponent(key)}/reveal`);
  }

  async setSecret(
    environment: string,
    key: string,
    payload: { value?: string; generate?: boolean },
  ): Promise<ApiResponse> {
    return this.request(`/api/sysdevops/secrets/${environment}/${encodeURIComponent(key)}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async deleteSecret(environment: string, key: string): Promise<ApiResponse> {
    return this.request(`/api/sysdevops/secrets/${environment}/${encodeURIComponent(key)}`, {
      method: 'DELETE',
    });
  }

  // Database explorer. Read-only by design — writes go through the
  // repositories, which also maintain uniqueness reservations, lookup items
  // and the stream-maintained aggregates.
  async getDatabaseOverview(): Promise<ApiResponse<{ table: TableOverview | null; entities: EntityDescription[] }>> {
    return this.request('/api/sysdevops/database/overview');
  }

  async getDatabaseRecords(
    entity: string,
    params: { limit?: number; cursor?: string | null } = {},
  ): Promise<ApiResponse<DatabaseRecordsPage>> {
    const query = new URLSearchParams();
    if (params.limit) query.set('limit', String(params.limit));
    if (params.cursor) query.set('cursor', params.cursor);
    const suffix = query.toString() ? `?${query}` : '';
    return this.request(`/api/sysdevops/database/entities/${encodeURIComponent(entity)}${suffix}`);
  }

  async getDatabaseRecord(entity: string, id: string): Promise<ApiResponse> {
    return this.request(
      `/api/sysdevops/database/entities/${encodeURIComponent(entity)}/${encodeURIComponent(id)}`,
    );
  }

  async getDatabaseMetrics(hours = 24): Promise<ApiResponse<{ series: MetricSeries[] }>> {
    return this.request(`/api/sysdevops/database/metrics?hours=${hours}`);
  }

  // Configuration state, not reachability. Lets the screen distinguish "nobody
  // has set this up" from "it is set up and failing", which look identical in
  // a generic fetch error.
  async getSonarQubeHealth(): Promise<ApiResponse<{ missing: string[] }>> {
    return this.request('/api/sysdevops/sonarqube/health');
  }
}

export default new ApiService(); 